import { Snapshot } from '@ember-data/store';
import { underscore } from '@ember/string';
import pick from 'lodash-es/pick';
import ApplicationSerializer from 'uplisting-frontend/serializers/application';

// The API accepts a narrow slice of the record per verb, and rejects the request outright rather
// than ignoring the rest: everything else on a task is derived or set by the backend.
const CREATE_ATTRIBUTES = [
  'title',
  'kind',
  'due_at',
  'description',
  'priority',
  'assignee_user_id',
];
const UPDATE_ATTRIBUTES = ['status', 'description', 'priority', 'due_at'];

export default class TaskSerializer extends ApplicationSerializer {
  serialize(snapshot: Snapshot, options: object) {
    const data = super.serialize(snapshot, options);

    // @ts-expect-error - we have no typings for the json api
    data.data.attributes = snapshot.id
      ? // @ts-expect-error - we have no typings for the json api
        this.#updateAttributes(snapshot, data.data.attributes)
      : // @ts-expect-error - we have no typings for the json api
        this.#createAttributes(snapshot, data.data.attributes);

    // The property travels as an attribute on create and cannot be changed afterwards, so the
    // relationships object is never something the API wants to read.
    // @ts-expect-error - we have no typings for the json api
    delete data.data.relationships;

    return data;
  }

  #createAttributes(
    snapshot: Snapshot,
    attributes: Record<string, unknown>,
  ): Record<string, unknown> {
    const propertyId = snapshot.belongsTo('property', { id: true });
    const multiUnitId = snapshot.belongsTo('multiUnit', { id: true });

    return {
      ...pick(attributes, CREATE_ATTRIBUTES),
      ...(propertyId ? { property_id: propertyId } : {}),
      ...(multiUnitId ? { multi_unit_id: multiUnitId } : {}),
    };
  }

  // A status has to travel on its own — sent alongside anything else the API answers 400 — so a
  // transition never carries the rest of the record with it.
  #updateAttributes(
    snapshot: Snapshot,
    attributes: Record<string, unknown>,
  ): Record<string, unknown> {
    const changed = Object.keys(snapshot.changedAttributes()).map((key) =>
      underscore(key),
    );

    if (changed.includes('status')) {
      return pick(attributes, ['status']);
    }

    return pick(
      attributes,
      UPDATE_ATTRIBUTES.filter((key) => changed.includes(key)),
    );
  }
}

declare module 'ember-data/types/registries/serializer' {
  interface SerializerRegistry {
    task: TaskSerializer;
  }
}
